import { Box, Heading, HStack, Text, VStack } from "@chakra-ui/react-v3";
import { FhirBundleSdk } from "@metriport/fhir-sdk";
import { useEffect, useMemo } from "react";
import { TableContent } from "../tables/table-content";
import { TableRow } from "../utils/tables-metadata";
import { getFeaturedCategories } from "./categories";
import { FiStar } from "react-icons/fi";

export type FilteredTableData = {
  tableType: string;
  filteredRows: TableRow[];
};

export type FeaturedSectionProps = {
  filteredTableData: FilteredTableData[];
  onRowClick: (row: TableRow, originalTableType: string) => void;
  fhirSdk: FhirBundleSdk;
  filterQuery: string;
  onPdfView: (docId: string) => void;
  onFeaturedDataChange?: (hasFeaturedData: boolean) => void;
};

/**
 * Featured section component that displays clinically important items
 * organized into categories and subcategories
 */
export function FeaturedSection({
  filteredTableData,
  onRowClick,
  fhirSdk,
  filterQuery,
  onPdfView,
  onFeaturedDataChange,
}: FeaturedSectionProps) {
  // Generate featured categories from filtered table data
  // No need for additional filtering - already done by parent
  const featuredCategories = useMemo(() => {
    if (filteredTableData.length === 0) return [];
    const rowsByCategory = new Map(filteredTableData.map(t => [t.tableType, t.filteredRows]));
    return getFeaturedCategories(rowsByCategory);
  }, [filteredTableData]);

  const hasFeaturedData = featuredCategories.length > 0;

  // Notify parent of featured data availability
  useEffect(() => {
    onFeaturedDataChange?.(hasFeaturedData);
  }, [hasFeaturedData, onFeaturedDataChange]);

  // If we're searching and there are no featured categories, skip rendering the section at all
  if (featuredCategories.length === 0 && filterQuery.trim()) {
    return null;
  }

  return (
    <Box w="100%" mb={2} borderRadius="lg" bg="bg.surface" px={6} py={6} boxShadow="sm">
      <HStack gap={2} mb={6}>
        <Heading size="xl" fontWeight="semibold" color="fg">
          Featured
        </Heading>
        <Box color="brand.9">
          <FiStar size={20} />
        </Box>
      </HStack>

      {featuredCategories.length === 0 ? (
        <Text color="fg.muted">No results found</Text>
      ) : (
        <VStack align="stretch" gap={6}>
          {featuredCategories.map(({ category, subCategories }) => (
            <Box key={category}>
              <Text fontSize="lg" fontWeight="semibold" color="fg" mb={4}>
                {category}
              </Text>

              <VStack align="stretch" gap={4}>
                {subCategories.map(({ subCategory, rows, originalTableType }) => (
                  <Box key={`${category}-${subCategory}`}>
                    <TableContent
                      tableType={originalTableType}
                      displayName={subCategory}
                      filteredRows={rows}
                      fhirSdk={fhirSdk}
                      filterQuery={filterQuery}
                      handleRowClick={onRowClick}
                      handlePdfView={onPdfView}
                    />
                  </Box>
                ))}
              </VStack>
            </Box>
          ))}
        </VStack>
      )}
    </Box>
  );
}
